import { Modal, Form, Input, InputNumber, Select, Button, Upload, message } from "antd";
import { generateSlug, isValidSlug, hasLeadingOrTrailingSpaces, isValidSlugLength } from "../../utils/SlugValidate";
import { useEffect, useState } from "react";
import CourseApiService from "../../services/CoursesApiService";
import { UploadOutlined } from "@ant-design/icons";

// Normalize the file upload event for Ant Design Form
const normFile = (e) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e && e.fileList;
};

const AddCourseModal = ({
  visible,
  onCancel,
  onOk,
  categories,
  form: externalForm,
}) => {
  const [form] = Form.useForm();
  const courseApi = CourseApiService;
  const [imageFile, setImageFile] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Auto-generate slug when name changes
  const handleNameChange = (e) => {
    const name = e.target.value;
    const generatedSlug = generateSlug(name);
    form.setFieldsValue({ slug: generatedSlug });
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setImageFile(null);
    }
  }, [visible, form]);

  const handleImageChange = ({ file }) => {
    if (!file) {
      setImageFile(null);
      return;
    }

    const currentFile = file.originFileObj || file;

    // Validate file type
    const isImage = currentFile.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return;
    }
    
    // Validate file size (2MB)
    const isLt2M = currentFile.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB!');
      return;
    }

    setImageFile(currentFile);
  };

  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);
      
      // Validate form
      const values = await form.validateFields();
      
      // Validate image
      if (!imageFile) {
        message.error('Please upload an image!');
        setSubmitLoading(false);
        return;
      }

      // Create FormData object
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('slug', values.slug);
      formData.append('description', values.description || '');
      formData.append('category', values.category);
      formData.append('price', values.price);
      formData.append('status', 'inactive');
      formData.append('author', values.author || '');
      formData.append('image', imageFile);

      // Call parent's onOk with formData
      await onOk(formData);
      
      // Reset form
      form.resetFields();
      setImageFile(null);
      
      // Close modal
      onCancel();
    } catch (error) {
      console.error('Error creating course:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.message) {
        message.error(error.message);
      } else {
        message.error('Failed to create course');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Modal
      title="Add Course"
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      okText="Add"
      confirmLoading={submitLoading}
    >
      <Form 
        form={form}
        layout="vertical"
        initialValues={{
          status: 'inactive',
        }}
      >
        <Form.Item
          label="Course Name"
          name="name"
          rules={[
            { required: true, message: 'Course name is required' },
            { max: 255, message: 'Course name must not exceed 255 characters' },
            { min: 1, message: 'Course name cannot be empty' },
            { whitespace: true, message: 'Course name cannot be only whitespace' }
          ]}
          tooltip="Enter a descriptive name for your course"
        >
          <Input 
            placeholder="e.g. Advanced JavaScript Programming" 
            onChange={handleNameChange}
          />
        </Form.Item>

        <Form.Item
          label="URL-Friendly"
          name="slug"
          rules={[
            { required: true, message: 'URL-Friendly name is required' },
            { max: 255, message: 'URL-Friendly name must not exceed 255 characters' },
            { min: 3, message: 'URL-Friendly name must be at least 3 characters' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                
                if (hasLeadingOrTrailingSpaces(value)) {
                  return Promise.reject('URL-Friendly name cannot contain leading or trailing spaces');
                }
                
                if (!isValidSlugLength(value)) {
                  return Promise.reject('URL-Friendly name must be at least 3 characters');
                }
                
                if (!isValidSlug(value)) {
                  return Promise.reject('URL-Friendly name must contain only lowercase letters, numbers, and hyphens');
                }

                return Promise.resolve();
              }
            }
          ]}
          tooltip="This will be used in the course URL. Will be auto-generated from course name"
        >
          <Input placeholder="e.g. advanced-javascript-programming" />
        </Form.Item>

        <Form.Item
          label="Author"
          name="author"
          rules={[
            { required: true, message: 'Author name is required' },
            { max: 255, message: 'Author name must not exceed 255 characters' },
            { whitespace: true, message: 'Author name cannot be only whitespace' }
          ]}
          tooltip="Enter the name of the course instructor/author"
        >
          <Input placeholder="e.g. John Smith" />
        </Form.Item>

        <Form.Item
          label="Price"
          name="price"
          required
          validateTrigger={['onChange', 'onBlur']}
          tooltip="Enter the course price (min: $0.01, max: $500)"
          rules={[
            {
              validator: async (_, value) => {
                if (value === null || value === undefined || value === '') {
                  throw new Error('Price is required');
                }
                if (typeof value === 'string') {
                  throw new Error('Price cannot contain letters or special characters');
                }
                if (typeof value !== 'number' || isNaN(value)) {
                  throw new Error('Price must be a valid number');
                }
                if (value <= 0) {
                  throw new Error('Price must be greater than 0');
                }
                if (value > 500) {
                  throw new Error('Price must not exceed $500');
                }
              }
            }
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0.01}
            max={500}
            precision={2}
            step={0.01}
            placeholder="Enter price (e.g. 29.99)"
            formatter={(value) => {
              if (!value) return '';
              return `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            }}
            parser={(value) => {
              const parsed = value.replace(/\$\s?|(,*)/g, '');
              if (parsed === '') return null;
              const num = parseFloat(parsed);
              return isNaN(num) ? null : num;
            }}
            onKeyPress={(e) => {
              const charCode = e.which ? e.which : e.keyCode;
              if (
                charCode !== 46 && // dấu chấm
                charCode > 31 && 
                (charCode < 48 || charCode > 57) // không phải số
              ) {
                e.preventDefault();
                message.error('Please enter numbers only. Letters and special characters are not allowed in price field.');
                return false;
              }
              return true;
            }}
          />
        </Form.Item>

        <Form.Item
          label="Category"
          name="category"
          rules={[
            { required: true, message: 'Category is required' }
          ]}
          tooltip="Select the most relevant category for your course"
        >
          <Select placeholder="Select a category">
            {categories.map((category) => (
              <Select.Option key={category._id} value={category._id}>
                {category.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[
            { max: 500, message: 'Description must not exceed 500 characters' }
          ]}
          tooltip="Provide a detailed description of what students will learn"
        >
          <Input.TextArea 
            rows={4} 
            placeholder="Describe your course content, learning outcomes, and target audience..."
          />
        </Form.Item>

        <Form.Item
          label="Course Image"
          name="imageUrl"
          rules={[
            { required: true, message: 'Course image is required' }
          ]}
          tooltip="Upload an attractive image that represents your course (Supported formats: JPG, PNG, JPEG. Max size: 5MB)"
        >
          <Upload
            listType="picture"
            maxCount={1}
            beforeUpload={() => false}
            onChange={handleImageChange}
            accept="image/*"
            onRemove={() => {
              setImageFile(null);
              return true;
            }}
          >
            <Button icon={<UploadOutlined />}>Upload Image</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddCourseModal;